import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFormContext, Controller } from 'react-hook-form';
import InputField from '#component/form/input';
import CheckBox from '#component/form/checkbox';
import CargoItemActions from '#component/shipment/form/cargo/cargo-item-actions';
import SelectionFields from '#component/shipment/form/cargo/selection-fields';
import { normalizeNumberField } from '#component/shipment/form/cargo/helper';
import DimensionsWeightFields from '#component/shipment/form/cargo/dimensions-weight-fields';
import { createTitle } from './helper';
import propTypes from './prop-types';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  TitleCnt,
  StyledTitle,
  StyledAvatar,
  StyledField,
  FieldsGroupCnt,
  CheckBoxCnt
} from './style';

/**
 * Renders MUI Accordion with fields related to cargo item.
 * Performs value normalization for number fields.
 * @returns {React.FC}
 */

const CargoItem = props => {
  const {
    index,
    item,
    member,
    goods = [],
    remove,
    numberOfItems,
    disabled,
    expanded,
    handleSetExpanded,
    shipmentId,
    isCopiedShipment
  } = props;
  const methods = useFormContext();
  const { control, setValue, formState } = methods || {};

  const projectId = useSelector(s => {
    const sh = s.shipment?.id ? s.shipment : { ...s.shipment, ...(s.shipmentForm?.shipment || {}) };
    return sh?.projectId || (typeof sh?.project === 'object' ? sh?.project?.id : sh?.project);
  }, shallowEqual);
  const containers = useSelector(s => s.projectsPackages?.[projectId] || [], shallowEqual);
  const [tareWeight, setTareWeight] = useState(
    (containers?.find(c => c.label === item.container) || {}).tareWeight
  );
  const [title, setTitle] = useState(createTitle(item.goods, item.container));
  const hasError = Boolean(formState?.isSubmitted && formState.errors?.cargo?.[index]);

  const initialProduct = item?.goods;
  const initialPackage = item?.container;

  const handleContainerChange = container => {
    setValue?.(`${member}.container`, container?.label || null);
    setValue?.(`${member}.weight`, container?.weight || null);
    setValue?.(`${member}.length`, container?.length || null);
    setValue?.(`${member}.width`, container?.width || null);
    setValue?.(`${member}.height`, container?.height || null);
    setTitle(createTitle(item.goods, container?.label));
    setTareWeight(container?.tareWeight);
  };

  const handleProductChange = product => {
    setValue?.(`${member}.goods`, product?.label || product?.name || product || null);
    setTitle(createTitle(product?.label || product?.name, item.container));
  };

  const handleDescriptionBlur = e => {
    setValue?.(`${member}.description`, e.target.value);
  };

  const handleQuantityBlur = e => {
    const value = normalizeNumberField(e.target.value);
    setValue?.(`${member}.quantity`, value);
  };

  const deleteCargoItem = event => {
    event.stopPropagation();
    remove(index);
  };

  const normalizeCheckBoxField = value => (value ? 1 : 0);

  return (
    <StyledAccordion
      hasError={hasError}
      expanded={expanded}
      onChange={handleSetExpanded(member)}
    >
      <StyledAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index + 1}a-content`}
        id={`panel${index + 1}a-header`}
      >
        <TitleCnt>
          <StyledAvatar>{index + 1}</StyledAvatar>
          <StyledTitle>{title}</StyledTitle>
        </TitleCnt>
        {numberOfItems > 1 &&
          <CargoItemActions deleteCargoItem={deleteCargoItem} disabled={disabled} />}
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <SelectionFields
          member={member}
          setValue={setValue}
          index={index}
          goods={goods}
          containers={containers}
          StyledField={StyledField}
          handleContainerChange={handleContainerChange}
          handleProductChange={handleProductChange}
          disabled={disabled}
          initialProduct={initialProduct}
          initialPackage={initialPackage}
          shipmentId={shipmentId}
          cargoItem={item}
          isCopiedShipment={isCopiedShipment}
          descriptionField={
            <Controller
              name={`${member}.description`}
              control={control}
              render={({ field, fieldState }) => (
                <InputField
                  {...field}
                  meta={fieldState}
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  onBlur={e => {
                    field.onBlur();
                    handleDescriptionBlur(e);
                  }}
                  disabled={disabled}
                />
              )}
            />
          }
        />
        <DimensionsWeightFields
          FieldsGroupCnt={FieldsGroupCnt}
          member={member}
          setValue={setValue}
          tareWeight={tareWeight}
          setTareWeight={setTareWeight}
          disabled={disabled || !!initialPackage}
        />
        <FieldsGroupCnt>
          <Controller
            name={`${member}.quantity`}
            control={control}
            render={({ field, fieldState }) => (
              <InputField
                {...field}
                meta={fieldState}
                label="Quantity"
                variant="outlined"
                onBlur={e => {
                  field.onBlur();
                  handleQuantityBlur(e);
                }}
                disabled={disabled}
              />
            )}
          />
          <CheckBoxCnt>
            <Controller
              name={`${member}.fragile`}
              control={control}
              render={({ field }) => (
                <CheckBox
                  {...field}
                  label="FRAGILE"
                  checked={!!field.value}
                  onChange={e => field.onChange(normalizeCheckBoxField(e.target.checked))}
                  disabled={disabled}
                />
              )}
            />
            <Controller
              name={`${member}.hazmat`}
              control={control}
              render={({ field }) => (
                <CheckBox
                  {...field}
                  label="HAZMAT"
                  checked={!!field.value}
                  onChange={e => field.onChange(normalizeCheckBoxField(e.target.checked))}
                  disabled={disabled}
                />
              )}
            />
          </CheckBoxCnt>
        </FieldsGroupCnt>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default React.memo(CargoItem);

CargoItem.propTypes = propTypes;
